import { Component, ElementRef, ViewChild } from '@angular/core';
import { LeftSideNavComponent } from '../../../shared/layout/app/left-menu/left-side-nav.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LoaderService } from '../../../shared/components/loader/loader.service';
import { VotingBllService } from '../voting-bll.service';
import { VideoDetailsModel } from '../voting.model';
import { AuthBllService } from '../../auth/auth-bll.service';
import { CommonModule } from '@angular/common';
import { ProfileBllService } from '../../profile/profile-bll.service';
import { UserEventStatusRespModel } from '../../profile/profile.model';
import { APIErrorModel, BaseResponse } from '../../../shared/models/base-response.model';
import { UserService } from '../../../core/services/user.service';
import { VirtualEventBllService } from '../../virtual-event/virtual-event-bll.service';
import { DeviceTypeService } from '../../../core/services/device-type.service';
import { HomeBllService } from '../../home/home-bll.service';
import { EventDetailsResponseModel } from '../../home/home.model';
declare var bootstrap: any;

@Component({
  selector: 'app-video-details',
  imports: [LeftSideNavComponent, CommonModule, RouterLink],
  templateUrl: './video-details.component.html',
  styleUrl: './video-details.component.scss'
})
export class VideoDetailsComponent {

  // Video details
  details: VideoDetailsModel = {
    id: 0,
    encodedId: '',
    primary_media_url: '',
    download_media_url: '',
    title: ''
  };
  videoId!: string;

  // User authentication status
  isAuthenticated: boolean = false;

  @ViewChild("SuccessModal") successModal!: ElementRef;
  @ViewChild("VoteChangedModal") voteChangedModal!: ElementRef;
  @ViewChild("ReplaceVoteModal") replaceVoteModal!: ElementRef;

  isVoted: boolean = false; // Indicates if the user has voted for this video

  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;
  hasRegisteredAsVirtualVisitor: boolean = false;

  userContestVoteCount: number = 0; // Number of votes cast by the user
  maxAllowedVoteCount: number = 3; // Maximum votes allowed per user

  replaceVote: boolean = false;

   // variable to hold the source of user(android_web/ios_web/web)
  source: string = "";
  eventDetails: EventDetailsResponseModel | null = null;

  constructor(
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private votingBllService: VotingBllService,
    private authBllService: AuthBllService,
    private router: Router,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private virtualBll: VirtualEventBllService,
    private deviceService: DeviceTypeService,
    private homeBll: HomeBllService
  ) { }

  ngOnInit() {
    this.videoId = this.route.snapshot.paramMap.get('participant_id') || '';

    this.authBllService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    this.loadVideoDetails();
    this.loadEventDetails();
  }

  // Load video details
  loadVideoDetails(): void {
    this.loaderService.show();
    const savedDetails = this.votingBllService.getSavedVideoDetails();

    // Fallback if nothing in storage
    this.details = savedDetails ?? {
      id: 0,
      encodedId: '',
      primary_media_url: '',
      download_media_url: '',
      title: '',
      is_voting_active: false
    };


    if (this.details.encodedId === this.videoId) {
      this.loaderService.hide();

      if (this.isAuthenticated) {
        this.callFetchVoteStatus();
        this.fetchUserEventStatus();

         // Get the source of user
        this.source = this.deviceService.getUserSource();
        this.callSubmitContestActivityLog('view_contest_participant', this.source);
      }
    }
  }

  /**
   * @description Fetches the user's event status and updates the user profile and service accordingly.
   */
  fetchUserEventStatus() {
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets:
              res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 ||
              res.data.event_my_tickets.tickets_assigned_for_me > 0

          });
        }

        /**
         * @description Check if the user is an event attendee and register as a virtual visitor if true.
         */
        const attendee = res?.data?.event_my_tickets?.event_attendee_details;
        if (!attendee) {
          this.registerVirtualVisitor();
        }

      })
      .catch((err: APIErrorModel) => {
        console.error("Error fetching user event status:", err);
      });
  }

  // Download file
  downloadFile(fileUrl: string) {

    if (!this.isAuthenticated) {
      this.authBllService.saveStorageValue("state", `/contests/details/${this.videoId}`);
      this.router.navigate(["/auth/init"]);
    } else {
      this.startDownload(fileUrl);

      this.callSubmitContestActivityLog('download_contest_participant_media', this.source);
    }

  }

  /**
   * @description Initiates the download of a file from the given URL.
   * @param fileUrl The URL of the file to be downloaded.
   */
  startDownload(fileUrl: string) {
    fetch(fileUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);

        // Extract extension from URL (best option)
        let extension = '';
        const urlParts = fileUrl.split('.');
        if (urlParts.length > 1) {
          extension = urlParts[urlParts.length - 1].split('?')[0]; 
        }

        // Fallback: Use MIME type from blob (if URL has no extension)
        if (!extension) {
          const mime = blob.type;
          const mimeParts = mime.split('/');
          extension = mimeParts[1] || 'file';
        }

        // Final filename
        const fileName = `${this.details.title || 'file'}.${extension}`;

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        console.error("File download failed");
      });

  }

  onClickVote(encodedId: string) {
    if (!this.isAuthenticated) {
      this.authBllService.saveStorageValue("state", `/contests/details/${this.videoId}`);
      this.router.navigate(["/auth/init"]);
      return;
    }

    // User is authenticated → decide Vote / Unvote
    if (this.isVoted) {
      const modalEl = this.voteChangedModal.nativeElement;
      const modal = new bootstrap.Modal(modalEl);
      modal.show();
    } else {
      if (this.userContestVoteCount >= this.maxAllowedVoteCount) {
        const modalEl = this.replaceVoteModal.nativeElement;
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      } else {
        this.callFetchVoteEntry(encodedId);
      }
    }
  }

  /**
   * @description Fetches the voting status of the user.
   */
  callFetchVoteStatus() {
    this.loaderService.show();
    this.votingBllService.fetchVoteStatus()
      .then((response) => {
        const votedIdsByUser = response.data.voted_participant_ids_by_user;
        const voteStatus = votedIdsByUser.includes(this.details.id)
        if (this.details.id && voteStatus) {
          this.isVoted = true;
        }
        this.userContestVoteCount = response.data.user_contest_vote_count;
        this.loaderService.hide();
      })
      .catch((error) => {
        console.error("Error fetching vote status:", error);
        this.loaderService.hide();
      });
  }

  /**
   * @param encodedId The encoded ID of the participant to vote for.
   */
  callFetchVoteEntry(encodedId: string, replaceVote?: boolean) {
    this.loaderService.show();
    this.votingBllService.fetchVoteEntry(encodedId)
      .then((response) => {
        this.loaderService.hide();

        this.userContestVoteCount = response.data.user_contest_vote_count;
        this.isVoted = true;

        if (!replaceVote) {
          this.showSuccessPopup();
        }
      })
      .catch((error) => {
        this.loaderService.hide();
        if (error.status === 410) {
          console.log("Voting period is not active.");
        } else if (error.status === 409) {
          const modalEl = this.voteChangedModal.nativeElement;
          const modal = new bootstrap.Modal(modalEl);
          modal.show();
        }
      });
  }

  /**
   * @description Displays the success popup modal.
   */
  showSuccessPopup() {
    const modalEl = this.successModal.nativeElement;
    // Reset the GIF to replay it
    const img: HTMLImageElement = modalEl.querySelector("img");
    if (img) {
      const newImg = img.cloneNode(true) as HTMLImageElement;
      const src = img.src.split("?")[0];
      newImg.src = `${src}?t=${Date.now()}`; // force reload

      img.replaceWith(newImg);
    }

    // Show the modal
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
  }

  /**
   * @description Unvotes a participant entry.
   * @param encodedId The encoded ID of the participant to unvote.
   */
  callUnVoteEntry(encodedId: string) {
    this.loaderService.show();
    this.votingBllService.fetchUnVoteEntry(encodedId)
      .then((response) => {
        this.loaderService.hide();
        this.isVoted = false;
        this.userContestVoteCount = response.data.user_contest_vote_count;
      })
      .catch((error) => {
        this.loaderService.hide();
        alert(error.message || "An error occurred while unvoting. Please try again.");
      });
  }

  /**
   * @description Registers the user as a virtual visitor for the event.
   */
  registerVirtualVisitor() {
    this.virtualBll.submitRegisterVirtualVisitor().then(() => {
      // Registration successful
      this.hasRegisteredAsVirtualVisitor = true;
    }).catch((error) => {
      this.hasRegisteredAsVirtualVisitor = false;
    });
  }

  /**
   * @description Navigates the user back to the participants list page.
   */
  goBack() {
    this.router.navigate(['contests/participants-list']);
  }


  callSubmitContestActivityLog(activityCode: string, source: string) {
    const payload = {
      activity_code: activityCode,
      activity_for_item_id: this.details.id,
      source: source
    }

     this.votingBllService.submitContestActivityLog(payload)
      .then((res) => {
        // No further action needed as we just log the activity
      }).catch((err) => {
        console.log("Error submitting contest activity log:", err);
      });
  }

  loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}
